import React from 'react';
import { Card, Button } from '@/components';
import { formatPhone, formatDateTime } from '@/utils/format';
import type { Order, PrintStatus } from '@shared/types';

interface DashboardOrdersTableProps {
  orders: Order[];
  searchValue: string;
  onSearchChange: (value: string) => void;
  onViewFiles?: (order: Order) => void;
  onRefresh?: () => void;
  onMarkOrder?: (orderId: string) => void;
  onMarkPrinted?: (order: Order) => void;
  /** Row number of the first order on this page (e.g. currentPage * PAGE_SIZE). */
  startIndex?: number;
  /** Total number of orders across all pages — used to number newest = highest. */
  totalCount?: number;
  isLoading?: boolean;
}

// Temporarily hide the print-status column, badges and Mark-printed actions.
// Flip to `true` to restore the print-status workflow.
const SHOW_PRINT_STATUS = false;

const PRINT_STATUS_BADGE: Record<PrintStatus, { label: string; className: string }> = {
  pending: { label: 'New', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  printing: { label: 'Printing', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  printed: { label: 'Printed', className: 'bg-green-50 text-green-700 border-green-200' },
};

const PRINT_STATUS_DOT: Record<PrintStatus, string> = {
  pending: 'bg-blue-500',
  printing: 'bg-amber-500',
  printed: 'bg-green-500',
};

const PrintStatusBadge: React.FC<{ status: PrintStatus }> = ({ status }) => {
  const badge = PRINT_STATUS_BADGE[status] ?? PRINT_STATUS_BADGE.pending;
  const dot = PRINT_STATUS_DOT[status] ?? PRINT_STATUS_DOT.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${badge.className}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {badge.label}
    </span>
  );
};

export const DashboardOrdersTable: React.FC<DashboardOrdersTableProps> = ({
  orders,
  onViewFiles,
  onMarkOrder,
  onMarkPrinted,
  startIndex = 0,
  totalCount = 0,
  isLoading = false,
}) => {
  return (
    <Card className="p-0 overflow-hidden">
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">S.No</th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Updated At</th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Contact</th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Files</th>
              <th className="px-6 py-3.5 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">View</th>
              <th className="px-6 py-3.5 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Save</th>
              {SHOW_PRINT_STATUS && (
                <th className="px-6 py-3.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Print Status</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={`skeleton-${i}`} className="animate-pulse">
                  <td className="px-6 py-4"><div className="h-4 bg-slate-100 rounded w-8 animate-pulse" /></td>
                  <td className="px-6 py-4"><div className="h-4 bg-slate-100 rounded w-32 animate-pulse" /></td>
                  <td className="px-6 py-4"><div className="h-4 bg-slate-100 rounded w-28 animate-pulse" /></td>
                  <td className="px-6 py-4"><div className="h-4 bg-slate-100 rounded w-20 animate-pulse" /></td>
                  <td className="px-6 py-4 text-center"><div className="h-8 bg-slate-100 rounded-lg w-16 mx-auto animate-pulse" /></td>
                  <td className="px-6 py-4 text-center"><div className="h-8 bg-slate-100 rounded-lg w-8 mx-auto animate-pulse" /></td>
                  {SHOW_PRINT_STATUS && <td className="px-6 py-4"><div className="h-6 bg-slate-100 rounded-full w-20 ml-auto animate-pulse" /></td>}
                </tr>
              ))
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan={SHOW_PRINT_STATUS ? 7 : 6} className="px-6 py-8 text-center text-slate-500">
                  No orders found
                </td>
              </tr>
            ) : (
              orders.map((order, rowIndex) => {
                const printStatus: PrintStatus = order.print_status ?? 'pending';
                // Use pre-calculated fields from view if available, fallback to manual calculation
                const fileCount = (order as any).file_count ?? (order.order_details?.length || 0);
                const totalPages = (order as any).total_pages ?? (order.order_details?.reduce((sum, f) => sum + (f.page_count && f.page_count > 0 ? f.page_count : 0), 0) || 0);
                const pagesCalculating = (order as any).pages_calculating ?? (order.order_details?.some((f) => f.page_count === 0) || false);
                return (
                <tr key={order.id} className="hover:bg-slate-50 transition-colors">
                  {/* S.NO (reverse: newest order has the highest number) */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-slate-400">{totalCount - startIndex - rowIndex}</div>
                  </td>

                  {/* UPDATED AT */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-slate-900 font-medium">
                      {formatDateTime(order.updated_at)}
                    </div>
                  </td>

                  {/* CONTACT */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-slate-950">{formatPhone(order.customer_phone)}</div>
                  </td>

                  {/* FILES */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5 text-sm text-slate-700">
                      <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span className="font-medium">{fileCount} file{fileCount === 1 ? '' : 's'}</span>
                      {totalPages > 0 && (
                        <span className="text-slate-400">·</span>
                      )}
                      {totalPages > 0 && (
                        <span className="text-slate-500">
                          {totalPages}{pagesCalculating ? '+' : ''} page{totalPages === 1 && !pagesCalculating ? '' : 's'}
                        </span>
                      )}
                    </span>
                  </td>

                  {/* VIEW */}
                  <td className="px-6 py-4 text-center">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onViewFiles?.(order)}
                    >
                      View
                    </Button>
                  </td>

                  {/* SAVE (BOOKMARK) */}
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => onMarkOrder?.(order.id)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-amber-50 hover:text-amber-500 transition-colors focus:outline-none"
                      aria-label={order.is_marked ? "Remove bookmark" : "Bookmark order"}
                    >
                      <svg className={`h-5 w-5 ${order.is_marked ? 'text-amber-500' : ''}`} fill={order.is_marked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                      </svg>
                    </button>
                  </td>

                  {/* PRINT STATUS + ACTION (last column) */}
                  {SHOW_PRINT_STATUS && (
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <PrintStatusBadge status={printStatus} />
                        {printStatus !== 'printed' && onMarkPrinted && (
                          <button
                            onClick={() => onMarkPrinted(order)}
                            className="inline-flex items-center rounded-lg border border-green-200 bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700 hover:bg-green-100 transition-colors focus:outline-none"
                            title="Mark this order as printed"
                          >
                            ✓ Mark printed
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards View */}
      <div className="block sm:hidden space-y-2 p-4">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={`skeleton-card-${i}`} className="border border-slate-200 rounded p-3 space-y-3 bg-white animate-pulse">
              <div className="flex justify-between items-center">
                <div className="h-3 bg-slate-100 rounded w-24 animate-pulse" />
                <div className="h-5 w-5 bg-slate-100 rounded animate-pulse" />
              </div>
              <div className="flex items-center justify-between">
                <div className="h-5 bg-slate-100 rounded w-32 animate-pulse" />
                {SHOW_PRINT_STATUS && <div className="h-5 bg-slate-100 rounded-full w-16 animate-pulse" />}
              </div>
              <div className="h-4 bg-slate-100 rounded w-20 animate-pulse" />
              <div className="flex gap-2 mt-2">
                <div className="h-8 bg-slate-100 rounded-lg flex-1 animate-pulse" />
                {SHOW_PRINT_STATUS && <div className="h-8 bg-slate-100 rounded-lg flex-1 animate-pulse" />}
              </div>
            </div>
          ))
        ) : orders.length === 0 ? (
          <div className="text-center text-slate-500 py-4">No orders found</div>
        ) : (
          orders.map((order) => {
            const printStatus: PrintStatus = order.print_status ?? 'pending';
            const files = order.order_details ?? [];
            const fileCount = files.length;
            const totalPages = files.reduce((sum, f) => sum + (f.page_count && f.page_count > 0 ? f.page_count : 0), 0);
            const pagesCalculating = files.some((f) => f.page_count === 0);
            return (
            <div key={`card-${order.id}`} className="border border-slate-200 rounded p-3 space-y-2 bg-white">
              <div className="flex justify-between items-start gap-2">
                <div className="text-xs text-slate-500 font-medium">
                  {formatDateTime(order.updated_at)}
                </div>
                <button
                  onClick={() => onMarkOrder?.(order.id)}
                  className="inline-flex h-5 w-5 items-center justify-center text-slate-400 hover:text-amber-500 transition-colors focus:outline-none"
                  aria-label={order.is_marked ? "Remove bookmark" : "Bookmark order"}
                >
                  {order.is_marked ? (
                    <svg className="h-4 w-4 text-amber-500" fill="currentColor" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4 text-slate-400 hover:text-amber-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                    </svg>
                  )}
                </button>
              </div>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-slate-950">{formatPhone(order.customer_phone)}</p>
                {SHOW_PRINT_STATUS && <PrintStatusBadge status={printStatus} />}
              </div>
              <p className="text-xs text-slate-600">
                {fileCount} file{fileCount === 1 ? '' : 's'}
                {totalPages > 0 && ` · ${totalPages}${pagesCalculating ? '+' : ''} page${totalPages === 1 && !pagesCalculating ? '' : 's'}`}
              </p>
              <div className="flex gap-2 mt-2">
                <Button variant="secondary" size="sm" className="flex-1" onClick={() => onViewFiles?.(order)}>
                  View files
                </Button>
                {SHOW_PRINT_STATUS && printStatus !== 'printed' && onMarkPrinted && (
                  <Button variant="secondary" size="sm" className="flex-1" onClick={() => onMarkPrinted(order)}>
                    Mark printed
                  </Button>
                )}
              </div>
            </div>
            );
          })
        )}
      </div>
    </Card>
  );
};

export default DashboardOrdersTable;
